'use client'

/**
 * HOME INFLUENCER_CARD PL widget — widget-of-widget child.
 *
 * Renders a single existing home IRL card from its own proto card data — the same v2 nested
 * pattern as the PDP IRL pair, but mapped to the home `influencerV2` section visuals (the PDP
 * registry maps this type to the non-DS `InfluencerCard` atom instead). Routes between the DS
 * card (`InfluencerV2CardDS`) and the legacy card via `shouldUseDesignSystem()`, exactly like the
 * non-PL `InfluencerV2` section router. The list-level state — play/mute, product fetch, popup —
 * lives on the parent INFLUENCER_SECTION container and arrives via `InfluencerV2CardContext`,
 * bound to this card's strip index. Outside that context (no section parent) the card has no
 * state to wire, so it renders nothing.
 */

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'
import { useMemo } from 'react'

import { InfluencerV2CardDS } from '@src/components/blocks/influencer-v2-ds'
import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface, PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { InfluencerV2Card } from '@src/SectionRenderer/sections/influencerV2/influencerV2Card'
import { shouldUseDesignSystem } from '@src/utils/should-use-design-system'

import { useInfluencerV2CardContext } from './influencer-v2-card-context'
import { adaptInfluencerV2Card } from './influencer-v2.adapter'

import type { InfluencerCardData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

function InfluencerV2CardWidgetComponent(props: PLTypedWidgetProps<InfluencerCardData>) {
  const card = props.data.influencerCard
  const ctx = useInfluencerV2CardContext()

  const irlItem = useMemo(() => (card ? adaptInfluencerV2Card(card) : null), [card])

  if (!irlItem || !ctx) return null

  // Same prop contract on both branches (InfluencerV2CardDSProps mirrors the legacy card's props).
  const CardComponent = shouldUseDesignSystem() ? InfluencerV2CardDS : InfluencerV2Card

  return (
    <CardComponent
      index={ctx.index}
      irlItem={irlItem}
      isPlaying={ctx.isPlaying}
      isMuted={ctx.isMuted}
      playVideo={ctx.playVideo}
      stopVideo={ctx.stopVideo}
      muteVideo={ctx.muteVideo}
      trackingMetaData={ctx.trackingMetaData}
      tracker={ctx.tracker}
      onClick={ctx.onCardClick}
    />
  )
}

export const InfluencerV2CardWidget: PLTypedWidgetInterface<InfluencerCardData, WidgetType.INFLUENCER_CARD> = {
  id: WidgetType.INFLUENCER_CARD,
  errorBoundary: PLBaseErrorBoundary,
  render: InfluencerV2CardWidgetComponent,
}
